import React from "react";

import styled from "styled-components";

import { parseMessageDoc } from "components/atoms/message/utils";

const Content = styled.span`
    color: inherit;
    font: inherit;
    line-height: inherit;
    white-space: pre-wrap;
    word-break: break-word;

    a {
        color: var(--color-Key);
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    p {
        margin: 0;
    }

    p + p {
        margin-top: 0.4em;
    }

    strong {
        font-weight: 700;
    }

    em {
        font-style: italic;
    }

    ul,
    ol {
        margin: 0.2em 0 0.2em 1.3em;
        padding: 0;
    }

    blockquote {
        margin: 0.3em 0;
        padding: 0.25em 0 0.25em 0.8em;
        border-left: 3px solid var(--color-Key);
        background: #f4faf8;
        color: #40534f;
    }

    code {
        padding: 1px 5px;
        border-radius: 4px;
        background: #f4f7f8;
        color: #d94b7f;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
        font-size: 0.92em;
    }
`;

const Mention = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 1.4em;
    padding: 0 4px;
    border-radius: 4px;
    background: #e8f5f1;
    color: var(--color-Key);
    font: inherit;
    font-weight: 700;
`;

const renderMarks = (children, marks = [], keyPrefix) =>
    marks.reduce((node, mark, index) => {
        const key = `${keyPrefix}-mark-${index}`;
        if (mark.type === "bold") return <strong key={key}>{node}</strong>;
        if (mark.type === "italic") return <em key={key}>{node}</em>;
        if (mark.type === "strike") return <s key={key}>{node}</s>;
        if (mark.type === "code") return <code key={key}>{node}</code>;
        if (mark.type === "link" && /^https?:\/\//i.test(mark.attrs?.href || "")) {
            return (
                <a key={key} href={mark.attrs.href} target="_blank" rel="noreferrer">
                    {node}
                </a>
            );
        }
        return node;
    }, children);

const renderNodes = (nodes = [], keyPrefix = "node") =>
    nodes.map((node, index) => renderNode(node, `${keyPrefix}-${index}`));

const renderNode = (node, key) => {
    if (!node) return null;

    if (node.type === "text") {
        return <React.Fragment key={key}>{renderMarks(node.text || "", node.marks, key)}</React.Fragment>;
    }

    if (node.type === "mention") {
        return (
            <Mention key={key} data-mention-id={node.attrs?.id}>
                @{node.attrs?.label || node.attrs?.id}
            </Mention>
        );
    }

    if (node.type === "paragraph") {
        return <p key={key}>{renderNodes(node.content, key)}</p>;
    }

    if (node.type === "hardBreak") {
        return <br key={key} />;
    }

    if (node.type === "bulletList") {
        return <ul key={key}>{renderNodes(node.content, key)}</ul>;
    }

    if (node.type === "orderedList") {
        return <ol key={key}>{renderNodes(node.content, key)}</ol>;
    }

    if (node.type === "listItem") {
        return <li key={key}>{renderNodes(node.content, key)}</li>;
    }

    if (node.type === "blockquote") {
        return <blockquote key={key}>{renderNodes(node.content, key)}</blockquote>;
    }

    if (node.type === "codeBlock") {
        return (
            <pre key={key}>
                <code>{renderNodes(node.content, key)}</code>
            </pre>
        );
    }

    return <React.Fragment key={key}>{renderNodes(node.content, key)}</React.Fragment>;
};

export default function MessageContent({ content = "" }) {
    const doc = parseMessageDoc(content);
    return <Content>{renderNodes(doc.content)}</Content>;
}
